// Maps learner domain data (every kind of ödev — şube / grup / bireysel / sınav — plus
// announcements) into the shared `CalendarEvent[]` the library `Calendar` primitive
// renders. Kept out of the presentational component so the calendar itself stays
// domain-agnostic and reusable. Used by both the learner dashboard calendar and the
// /announcements calendar so the two read identically.
//
// Support assignments arrive INSIDE activeContentList (LearnerApp normalizes them into it),
// so there is no second list argument any more — one feed, one loop, one place where a
// row's kind decides its words. The kind label and icon come from
// AssignmentKindUtilization, the same table the row chips read, so the calendar and the
// list can never disagree about what a row is called.
import { CalendarEvent } from "../library";
import i18n from "../language/i18n";
import { retrieveSubjectStyle } from "./SubjectUtilization";
import {
  assignmentKindIconName,
  assignmentKindLabelKey,
} from "./AssignmentKindUtilization";

// Announcement scope (announcement.type) → accent ink/tint, mirroring the scope
// coding used on the announcements list rows.
const ANNOUNCEMENT_SCOPE_STYLE: Record<string, { ink: string; tint: string }> = {
  USER: { ink: "var(--accent-coral-text, #D9613F)", tint: "var(--accent-coral-bg)" },
  USER_PROFILE: { ink: "var(--accent-coral-text, #D9613F)", tint: "var(--accent-coral-bg)" },
  BRANCH: { ink: "var(--brand-dark)", tint: "var(--brand-50)" },
  GRADE: { ink: "var(--accent-gold-text)", tint: "var(--accent-gold-bg)" },
  SEASON: { ink: "var(--accent-purple-text)", tint: "var(--accent-purple-bg)" },
  INSTITUTION: { ink: "var(--accent-purple-text)", tint: "var(--accent-purple-bg)" },
  CAMPUS: { ink: "var(--accent-purple-text)", tint: "var(--accent-purple-bg)" },
  BRAND: { ink: "var(--accent-purple-text)", tint: "var(--accent-purple-bg)" },
};
const announcementScopeStyle = (type: string) =>
  ANNOUNCEMENT_SCOPE_STYLE[type] || { ink: "var(--brand-dark)", tint: "var(--brand-50)" };

export interface BuildLearnerCalendarEventsArgs {
  activeContentList?: any[];
  announcementDTOList?: any[];
}

// The kind label, rendered through the shared kind table so "Destek ödevi" is the same
// word here as on the row chip. An unknown type never reaches this (the caller only
// builds an event for the kinds below), but it degrades to an empty label rather than
// printing a raw enum at a learner.
const kindLabel = (type: string): string => {
  const key = assignmentKindLabelKey(type);
  return key ? i18n.t(key) : "";
};

// Build the unified event list. Each dated item contributes one event per date
// it owns: assignments (şube / grup) and exams get a `start` (beginAt) and a `due`
// (endAt) marker; announcements get a single `posted` marker (createdAt).
export const buildLearnerCalendarEvents = ({
  activeContentList = [],
  announcementDTOList = [],
}: BuildLearnerCalendarEventsArgs): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  activeContentList.forEach((c) => {
    if (c.type === "BRANCH_ASSIGNMENT") {
      const s = retrieveSubjectStyle(c.coterieType);
      const base = {
        id: `assignment-${c.id}`,
        title: c.name || kindLabel(c.type),
        kindLabel: kindLabel(c.type),
        iconName: assignmentKindIconName(c.type) as string,
        ink: s.ink,
        tint: s.tint,
        route: `/branch-assignments/${c.id}`,
      };
      if (c.beginAt) events.push({ ...base, ts: c.beginAt, marker: "start" });
      if (c.endAt) events.push({ ...base, ts: c.endAt, marker: "due" });
    } else if (c.type === "SUPPORT_ASSIGNMENT") {
      // Same loop as its siblings now that LearnerApp normalizes group rows into
      // activeContentList. The id is kind-scoped because the four kinds are four
      // independent primary-key sequences that can collide numerically.
      const s = retrieveSubjectStyle(c.coterieType);
      const base = {
        id: `support-assignment-${c.id}`,
        title: c.name || kindLabel(c.type),
        kindLabel: kindLabel(c.type),
        iconName: assignmentKindIconName(c.type) as string,
        ink: s.ink,
        tint: s.tint,
        route: `/support-assignments/${c.id}`,
      };
      if (c.beginAt) events.push({ ...base, ts: c.beginAt, marker: "start" });
      if (c.endAt) events.push({ ...base, ts: c.endAt, marker: "due" });
    } else if (c.type === "STANDARD_EXAM") {
      const cse = c.standardExamDTO;
      const s = retrieveSubjectStyle(cse?.standardExamType);
      const base = {
        id: `exam-${c.id}`,
        title: cse?.name || kindLabel(c.type),
        kindLabel: kindLabel(c.type),
        iconName: assignmentKindIconName(c.type) as string,
        ink: s.ink,
        tint: s.tint,
        route: `/standard-exam-applications/${c.id}`,
      };
      if (c.beginAt) events.push({ ...base, ts: c.beginAt, marker: "start" });
      if (c.endAt) events.push({ ...base, ts: c.endAt, marker: "due" });
    }
  });

  announcementDTOList.forEach((a) => {
    if (!a.createdAt) return;
    const s = announcementScopeStyle(a.type);
    events.push({
      id: `announcement-${a.id}`,
      ts: a.createdAt,
      marker: "posted",
      title: a.name ? i18n.t(a.name) : i18n.t("announcement"),
      kindLabel: i18n.t("announcement"),
      iconName: "announcement",
      ink: s.ink,
      tint: s.tint,
      route: "/announcements",
    });
  });

  return events;
};
